import { t } from 'i18next'
import { FC } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Trans } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

import { Add, Delete } from '@mui/icons-material'
import { Box, Divider, Grid, InputAdornment, TextField, Typography } from '@mui/material'

import { ActionButton } from '../../../../components/ActionButton/ActionButton'
import { ErrorStatusCircle } from '../../../../components/ErrorStatusCircle/ErrorStatusCircle'
import { isNumber } from '../../../../helpers/isNumber'
import { CurrencyBundle } from '../../../../types/Currency'
import { pricePointDefaultCurrency } from '../../types/PricePoint'
import '../CurrencyDisplay/PricingDataFlexContainer.scss'

type CurrencyAdderProps = {
  currencies: CurrencyBundle[] | undefined
  onChange: (currency: CurrencyBundle[]) => void
}

export const CurrencyAdder: FC<CurrencyAdderProps> = ({ currencies, onChange }) => {
  const defaultValues = { name: '', currencyDescription: '', purposeDescription: '', values: [{ [pricePointDefaultCurrency]: '', value: '' }] }
  const {
    control,
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: defaultValues,
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'values' })

  const formData = watch()

  const addRow = () => append({ [pricePointDefaultCurrency]: '', value: '' })

  const addNewCurrency = () => {
    const { name, currencyDescription, purposeDescription, values } = getValues()

    const rows = (values ?? [])
      .filter((x) => isNumber(x[pricePointDefaultCurrency]) && isNumber(x.value))
      .map((x) => ({
        usd: Number(x[pricePointDefaultCurrency]),
        value: Number(x.value),
      }))

    onChange([
      ...(currencies ?? []),
      {
        id: uuidv4(),
        name: name.trim(),
        currencyDescription: currencyDescription?.trim() || '',
        purposeDescription: purposeDescription?.trim() || '',
        values: rows,
      },
    ])
    reset(defaultValues)
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h3">
          <Trans i18nKey="internal-live-events:add_new_currency" />
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <TextField
                required
                type="text"
                value={field.value}
                onChange={field.onChange}
                variant="outlined"
                size="small"
                label={t('internal-live-events:currency_name')}
                fullWidth
                error={includesName(formData, currencies)}
                InputProps={{
                  endAdornment: includesName(formData, currencies) ? (
                    <InputAdornment position="end">
                      <ErrorStatusCircle errorText={t('internal-live-events:duplicate_currency_error_message')} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            )
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="currencyDescription"
          control={control}
          render={({ field }) => {
            return (
              <TextField
                type="text"
                value={field.value || ''}
                onChange={field.onChange}
                variant="outlined"
                size="small"
                label={t('live-events:use')}
                fullWidth
                multiline
                minRows={2}
              />
            )
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="purposeDescription"
          control={control}
          render={({ field }) => {
            return (
              <TextField
                type="text"
                value={field.value || ''}
                onChange={field.onChange}
                variant="outlined"
                size="small"
                label={t('live-events:how_to_get')}
                fullWidth
                multiline
                minRows={2}
              />
            )
          }}
        />
      </Grid>

      <Divider sx={{ width: '100%', mt: 2 }} />

      {fields.map((row, index) => (
        <Grid container spacing={2} sx={{ ml: 0.25, mt: 0.3 }} key={row.id}>
          <Grid item sm={4}>
            <Controller
              name={`values.${index}.${pricePointDefaultCurrency}`}
              control={control}
              rules={{ required: true, validate: (value) => isNumber(value ?? '') && Number(value ?? '0') > 0 }}
              render={({ field }) => {
                return (
                  <TextField
                    required
                    type="text"
                    value={field.value}
                    onChange={field.onChange}
                    variant="outlined"
                    size="small"
                    label={t('internal-live-events:usd')}
                    fullWidth
                    error={!!field.value && (!isNumber(field.value) || Number(field.value) <= 0)}
                    InputProps={{
                      endAdornment:
                        !!field.value && (!isNumber(field.value) || Number(field.value) <= 0) ? (
                          <InputAdornment position="end">
                            <ErrorStatusCircle errorText={t('internal-live-events:enter_number_error_message')} />
                          </InputAdornment>
                        ) : null,
                    }}
                  />
                )
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Controller
              name={`values.${index}.value`}
              control={control}
              rules={{ required: true, validate: (value) => isNumber(value ?? '') && Number(value ?? '0') > 0 }}
              render={({ field }) => (
                <TextField
                  required
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  variant="outlined"
                  size="small"
                  label={t('internal-live-events:in_game_currency')}
                  fullWidth
                  error={!!field.value && (!isNumber(field.value) || Number(field.value) <= 0)}
                  inputMode="decimal"
                  InputProps={{
                    endAdornment:
                      !!field.value && (!isNumber(field.value) || Number(field.value) <= 0) ? (
                        <InputAdornment position="end">
                          <ErrorStatusCircle errorText={t('internal-live-events:enter_number_error_message')} />
                        </InputAdornment>
                      ) : null,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item sm={4}>
            <Box sx={{ display: 'inline-flex', ml: 2, mt: 0.25 }}>
              <Box sx={{ mr: 1 }}>
                <ActionButton onClick={addRow}>
                  <Add fontSize="small" />
                  <Typography>{t('internal-live-events:add_more')}</Typography>
                </ActionButton>
              </Box>
              {fields.length > 1 && (
                <Box>
                  <ActionButton onClick={() => remove(index)} color="warning">
                    <Delete fontSize="small" />
                    <Typography>{t('common:remove')}</Typography>
                  </ActionButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      ))}
      <Box sx={{ ml: 2.5, mt: 2 }}>
        <ActionButton onClick={addNewCurrency} disabled={!(isValid && !includesName(formData, currencies))}>
          <Add fontSize="small" />
          <Typography>Add Currency</Typography>
        </ActionButton>
      </Box>
    </>
  )
}

export const includesName = (formData: any, currencies: CurrencyBundle[] | undefined) => {
  if (formData.name.trimEnd().length === 0) return false
  const result = currencies?.some((currency) => currency.name === formData.name.trimEnd())
  if (result) return result
  return false
}
